/**
 * Profile Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import { Check, X } from 'lucide-react';

const modules = [
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'pdv', name: 'PDV / Caixa' },
  { id: 'products', name: 'Produtos' },
  { id: 'categories', name: 'Categorias' },
  { id: 'combos', name: 'Combos' },
  { id: 'customers', name: 'Clientes' },
  { id: 'tables', name: 'Mesas' },
  { id: 'orders', name: 'Comandas' },
  { id: 'cashiers', name: 'Caixas' },
  { id: 'users', name: 'Funcionários' },
  { id: 'profiles', name: 'Perfis' },
  { id: 'payment_methods', name: 'Formas de Pagamento' },
  { id: 'reports', name: 'Relatórios' },
];

const defaultPermissions = modules.map(m => ({
  module: m.id,
  can_view: false,
  can_create: false,
  can_edit: false,
  can_delete: false,
}));

const profileSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  description: z.string().max(200, 'Descrição muito longa').optional(),
});

export function ProfileModal({ open, onOpenChange, profile, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!profile?.id;
  const [permissions, setPermissions] = useState(defaultPermissions);

  const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  // Reset form when modal opens or profile changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (profile) {
      reset({
        name: profile.name || '',
        description: profile.description || '',
      });
      // Map existing permissions
      const perms = modules.map(m => {
        const existing = profile.permissions?.find(p => p.module === m.id);
        return existing || {
          module: m.id,
          can_view: false,
          can_create: false,
          can_edit: false,
          can_delete: false,
        };
      });
      setPermissions(perms);
    } else {
      reset({
        name: '',
        description: '',
      });
      setPermissions(defaultPermissions);
    }
  }, [open, profile, reset]);

  const updatePermission = (moduleId, field, value) => {
    setPermissions(prev => prev.map(p =>
      p.module === moduleId ? { ...p, [field]: value } : p
    ));
  };

  const toggleAllForModule = (moduleId, value) => {
    setPermissions(prev => prev.map(p =>
      p.module === moduleId
        ? { ...p, can_view: value, can_create: value, can_edit: value, can_delete: value }
        : p
    ));
  };

  const getModuleName = (id) => modules.find(m => m.id === id)?.name || id;

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
    if (draft.permissions) {
      setPermissions(draft.permissions);
    }
  };

  const onSubmit = (data) => {
    onSave({
      ...data,
      permissions,
    });
  };

  // Track form dirty state including permissions changes
  const [permissionsDirty, setPermissionsDirty] = useState(false);
  const isFormDirty = isDirty || permissionsDirty;

  const updatePermissionWithTracking = (moduleId, field, value) => {
    setPermissionsDirty(true);
    updatePermission(moduleId, field, value);
  };

  const toggleAllForModuleWithTracking = (moduleId, value) => {
    setPermissionsDirty(true);
    toggleAllForModule(moduleId, value);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Perfil' : 'Novo Perfil'}
      description={isEditing ? 'Atualize os dados do perfil' : 'Configure as permissões do novo perfil'}
      size="lg"
      formId="profile"
      entityId={profile?.id}
      isDirty={isFormDirty}
      formValues={{ ...formValues, permissions }}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome do Perfil *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Ex: Gerente"
            data-testid="profile-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Descrição do perfil"
            rows={2}
          />
        </div>

        <div className="permissions-table-container">
          <table className="permissions-table">
            <thead>
              <tr>
                <th className="text-left">Módulo</th>
                <th className="text-center">Ver</th>
                <th className="text-center">Criar</th>
                <th className="text-center">Editar</th>
                <th className="text-center">Excluir</th>
                <th className="text-center">Todos</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map(perm => {
                const allChecked = perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;
                return (
                  <tr key={perm.module}>
                    <td className="font-medium">{getModuleName(perm.module)}</td>
                    <td className="text-center">
                      <Checkbox
                        checked={perm.can_view}
                        onCheckedChange={(v) => updatePermissionWithTracking(perm.module, 'can_view', v)}
                      />
                    </td>
                    <td className="text-center">
                      <Checkbox
                        checked={perm.can_create}
                        onCheckedChange={(v) => updatePermissionWithTracking(perm.module, 'can_create', v)}
                      />
                    </td>
                    <td className="text-center">
                      <Checkbox
                        checked={perm.can_edit}
                        onCheckedChange={(v) => updatePermissionWithTracking(perm.module, 'can_edit', v)}
                      />
                    </td>
                    <td className="text-center">
                      <Checkbox
                        checked={perm.can_delete}
                        onCheckedChange={(v) => updatePermissionWithTracking(perm.module, 'can_delete', v)}
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => toggleAllForModuleWithTracking(perm.module, !allChecked)}
                      >
                        {allChecked ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </FormModal>
  );
}

export default ProfileModal;
